import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/Button'
import { Kicker } from '@/components/Kicker'
import { HeroCard } from '@/components/HeroCard'
import { PACK_COLORS, PACK_LABELS, type PackKey } from '@/design-system/tokens'
import { IconForPack } from '@/design-system/icons'
import { useAppStore } from '@/stores/appStore'
import { supabase } from '@/lib/supabase'

/**
 * OnboardingScreen — signed-in user with no family memberships.
 * Explains the Person Graph and offers "Start a family" or accept an invite.
 */
export function OnboardingScreen() {
  const nav = useNavigate()
  const user = useAppStore((s) => s.user)

  const featured: PackKey[] = ['early_care', 'shopping', 'health', 'school']

  return (
    <div className="mx-auto flex min-h-svh w-full max-w-lg flex-col bg-bg-base">
      <div className="px-5 pt-safe pt-8">
        <Kicker>Welcome</Kicker>
        <h1 className="mt-2 font-serif italic text-hero text-text-primary">Everything that matters. In one place.</h1>
        <p className="mt-3 text-body text-text-secondary">
          Wren is a Person Graph — a shared, offline-first record of your family. Start with pregnancy and daily care. Add
          school, sports, health, and shopping as your child grows.
        </p>

        <div className="mt-6">
          <HeroCard
            bloom="mint"
            kicker="What you get"
            headline="A record of your child, not a tracker of feedings"
            subhead="Twenty years, one graph. Multiple caregivers. Yours."
          />
        </div>

        <div className="mt-6">
          <Kicker>Domains available today</Kicker>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {featured.map((pack) => {
              const Icon = IconForPack[pack]
              const color = PACK_COLORS[pack].light
              return (
                <div
                  key={pack}
                  className="rounded-r-sm border border-divider-subtle bg-bg-elevated p-3 flex items-center gap-2"
                >
                  <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ background: color + '22', color }}>
                    <Icon size={18} />
                  </div>
                  <div className="text-body text-text-primary font-medium">{PACK_LABELS[pack]}</div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="mt-8 space-y-3">
          <Button
            variant="primary"
            size="lg"
            packColor={PACK_COLORS.early_care.light}
            className="w-full"
            onClick={() => nav('/bootstrap')}
          >
            Start my family
          </Button>
          <div className="text-center text-caption text-text-tertiary">
            Signed in as <span className="text-text-secondary">{user?.email}</span>
          </div>
          <button
            type="button"
            onClick={() => supabase.auth.signOut()}
            className="w-full text-caption text-text-tertiary underline underline-offset-2 hover:text-text-secondary"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}
